/** @format */

import { useCallback, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { icons, Folder, Search, X } from 'lucide-react';
import { cn, Popover, PopoverContent, PopoverTrigger } from '@clarix/ui';
import { ICON_CATEGORIES } from '@clarix/utils';

const DEFAULT_ICON = 'Folder';

// ── Dynamic Icon Renderer ─────────────────────────────────────────────

interface DynamicIconProps {
  name: string;
  className?: string;
  strokeWidth?: number;
  style?: CSSProperties;
}

export function DynamicIcon({ name, className, strokeWidth = 1.5, style }: DynamicIconProps) {
  const IconComponent = icons[name as keyof typeof icons];
  if (!IconComponent) {
    return <Folder className={className} strokeWidth={strokeWidth} style={style} />;
  }
  return <IconComponent className={className} strokeWidth={strokeWidth} style={style} />;
}

// ── Icon Picker Content ───────────────────────────────────────────────

interface IconPickerContentProps {
  value: string | null;
  onSelect: (iconName: string) => void;
  /** When provided, renders a "Clear icon" footer that resets to no icon. */
  onClear?: () => void;
  className?: string;
}

/**
 * The picker body — search, category tabs, icon grid. Split out of
 * `IconPicker` so it can be hosted by any surface: `IconPicker`'s own
 * Popover, or a Radix `DropdownMenuSubContent` (a Popover cannot be nested
 * inside a dropdown submenu). Owns only its search/category state.
 */
export function IconPickerContent({
  value,
  onSelect,
  onClear,
  className,
}: IconPickerContentProps) {
  const [activeCategory, setActiveCategory] = useState(0);
  const [search, setSearch] = useState('');
  const currentIcon = value ?? DEFAULT_ICON;

  const filteredIcons = useMemo(() => {
    if (!search.trim()) return null;
    const lower = search.toLowerCase();
    const allIcons = ICON_CATEGORIES.flatMap((cat) => cat.icons);
    return [...new Set(allIcons)].filter((name) => name.toLowerCase().includes(lower));
  }, [search]);

  const activeCat = ICON_CATEGORIES[activeCategory] ?? ICON_CATEGORIES[0]!;
  const displayIcons = filteredIcons ?? activeCat.icons;

  return (
    <div className={cn('w-[320px]', className)}>
      {/* Search */}
      <div className="border-b border-gray-100 p-2">
        <div className="relative">
          <Search
            className="text-muted-foreground absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2"
            strokeWidth={1.5}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            // Radix menus hijack printable keys for typeahead, which would
            // steal every keystroke when this lives in a DropdownMenuSubContent.
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Search icons..."
            aria-label="Search icons"
            className="bg-surface placeholder:text-muted-foreground h-7 w-full rounded-md border border-gray-200 pl-7 pr-7 text-lg focus:outline-none focus:ring-1 focus:ring-indigo-300"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label="Clear search"
              className="text-muted-foreground hover:text-foreground absolute right-1.5 top-1/2 -translate-y-1/2"
            >
              <X className="h-3 w-3" strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {/* Category tabs (hidden when searching) */}
      {!filteredIcons && (
        <div className="flex gap-0.5 overflow-x-auto border-b border-gray-100 px-1 pt-1">
          {ICON_CATEGORIES.map((cat, idx) => (
            <button
              key={cat.label}
              type="button"
              onClick={() => setActiveCategory(idx)}
              className={cn(
                'whitespace-nowrap rounded-t-md px-2.5 py-1.5 text-lg font-medium transition-colors',
                activeCategory === idx
                  ? 'text-foreground bg-gray-100'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}

      {/* Icon grid */}
      <div className="max-h-[200px] overflow-y-auto p-2">
        {displayIcons.length === 0 ? (
          <div className="text-muted-foreground py-4 text-center text-lg">No icons found</div>
        ) : (
          <div className="grid grid-cols-8 gap-0.5">
            {displayIcons.map((iconName) => {
              const isSelected = currentIcon === iconName;
              return (
                <button
                  key={iconName}
                  type="button"
                  onClick={() => onSelect(iconName)}
                  title={iconName}
                  aria-label={iconName}
                  aria-pressed={isSelected}
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-md transition-all',
                    isSelected
                      ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-500 ring-offset-1'
                      : 'hover:text-foreground text-gray-600 hover:scale-110 hover:bg-gray-100',
                  )}
                >
                  <DynamicIcon name={iconName} className="h-4 w-4" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {onClear && (
        <div className="border-t border-gray-100 px-2 py-1.5">
          <button
            type="button"
            onClick={onClear}
            disabled={!value}
            className="text-muted-foreground hover:text-foreground text-sm disabled:opacity-50"
          >
            Clear icon
          </button>
        </div>
      )}
    </div>
  );
}

// ── Icon Picker Component ─────────────────────────────────────────────

interface IconPickerProps {
  value: string | null;
  onChange: (iconName: string) => void;
}

export function IconPicker({ value, onChange }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const currentIcon = value ?? DEFAULT_ICON;

  const handleSelect = useCallback(
    (iconName: string) => {
      onChange(iconName);
      setOpen(false);
    },
    [onChange],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'border-input bg-background flex h-7 w-full items-center gap-2.5 rounded-md border px-3',
            'hover:bg-accent/50 focus-visible:ring-ring text-xl transition-colors focus-visible:outline-none focus-visible:ring-2',
          )}
        >
          <DynamicIcon name={currentIcon} className="text-foreground h-4 w-4" />
          <span className="text-muted-foreground text-lg">
            {value ? currentIcon : 'Choose'}
          </span>
          <Search className="text-muted-foreground ml-auto h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
      </PopoverTrigger>

      {/* Remount on close so the search box resets between openings. */}
      <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
        <IconPickerContent value={value} onSelect={handleSelect} />
      </PopoverContent>
    </Popover>
  );
}
